import { ClientApi } from "@/api/ClientApi";
import { CurrencyApi } from "@/api/CurrencyApi";
import { CUSTOM_ELEMENT, HIDDEN_INPUT_TYPE, PHONE_INPUT_TYPE, RADIO_INPUT_TYPE, SELECT_INPUT_TYPE, VALIDATION_ERROR_MESSAGE, Yup } from "@mavichpe/dynamic-forms";
import { INVOICE_CREDIT_TYPE_CODE, INVOICE_HOLDING_TYPE_CODE, SETTLEMENT_TYPES, TIPOS_DE_IDENTIFICACION } from "@utils/Constants";
import { generateCurrencyOptionLabel } from "@utils/Currency";
import { getEmailYupValidation, withIdentificationFormat, withPhoneFormat } from "@utils/Forms";
import { CurrencyIdPubSub, OpenAddClientPubSub, OpenSearchClientByIdentificationPubSub } from "@utils/pubsubs/PubSub";
import { Button } from "reactstrap";

const InvoiceDataFormConfig = {
    sections: [
        {
            "fields": [
                [
                    {
                        "type": RADIO_INPUT_TYPE,
                        "name": "tipo",
                        "validation": Yup.string().required(),
                        "label": "Tipo Liquidacion",
                        "isHorizontalValues": true,
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 3
                            }
                        },
                        "options": SETTLEMENT_TYPES,
                        "defaultValue": 0,
                    },
                    {
                        "type": "text",
                        "name": "reference_number",
                        "label": "Numero Referencia",
                        "visibilityHiddenIf": () => true, // by default this input is hidden, suscrption data make it visible
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 3
                            }
                        },
                    },
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "currencyId",
                        "label": "Moneda",
                        "optionsFunction": () => CurrencyApi.optionsList({}, (i) => generateCurrencyOptionLabel(i)),
                        "onChangePubSub": (value: string) => CurrencyIdPubSub.publish({ currencyId: value }),
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 3
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                    {
                        "type": "number",
                        "isDecimal": true,
                        "name": "exchange_rate",
                        "min": "0",
                        "step": "0.01",
                        "label": "Tipo de Cambio",
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 3,
                            "xl": 2,
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": "number",
                        "name": "plazo",
                        "min": "1",
                        "isHorizontal": true,
                        "suffix": "dias",
                        "visibleIf": {
                            "tipo": INVOICE_CREDIT_TYPE_CODE
                        },
                        "label": "Plazo",
                        "labelSizing": {
                            "sm": 2,
                            "md": 1,
                        },
                        "sizing": {
                            "sm": 4,
                            "md": 3,
                            "lg": 2,
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                    {
                        "type": "money",
                        "name": "monto_inicial",
                        "isHorizontal": true,
                        "min": "1",
                        "visibleIf": {
                            "tipo": INVOICE_HOLDING_TYPE_CODE
                        },
                        "label": "Abono Inicial",
                        "labelSizing": {
                            "sm": 2,
                            "md": 2,
                            "lg": 2,
                            "xl": 1,
                        },
                        "sizing": {
                            "sm": 4,
                            "md": 3,
                            "lg": 2,
                        }
                    },
                ],
            ]
        },
        {
            title: "Datos de Cliente",
            "fields": [
                [
                    {
                        "type": "text",
                        "name": "client_code",
                        "exludeFromSubmit": true,
                        "label": "Cod",
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 2
                            }
                        }
                    },
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "clientId",
                        "label": "Cliente",
                        "optionsFunction": () => ClientApi.optionsList(),
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 9
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                    {
                        "type": CUSTOM_ELEMENT,
                        "label": " ",
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 1
                        },
                        "element": <Button
                            className="mt-2"
                            block
                            color="primary"
                            type="button"
                            onClick={() => OpenAddClientPubSub.publish({ openOffCanvas: true })}>
                            Crear
                        </Button>
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "nombre_factura",
                        "label": "Nombre Facturacion",
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 12
                            }
                        }
                    },
                ],
                [
                    {
                        // La razon social se guarda aparte del nombre de facturacion porque
                        // Hacienda espera el nombre legal en Receptor/Nombre y el comercial
                        // en Receptor/NombreComercial. No se edita: sale de la ficha maestra.
                        "type": HIDDEN_INPUT_TYPE,
                        "name": "razon_social",
                    },
                ],
                [
                    {
                        "type": PHONE_INPUT_TYPE,
                        "name": "tel1",
                        "label": "Telefono 1",
                        "validation": withPhoneFormat(Yup.string().notRequired()),
                    },
                    {
                        "type": PHONE_INPUT_TYPE,
                        "name": "tel2",
                        "label": "Telefono 2",
                        "validation": withPhoneFormat(Yup.string().notRequired()),
                    },
                    {
                        "type": "email",
                        "name": "email",
                        "label": "Email",
                        "validation": getEmailYupValidation()
                    },
                ],
                [
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "tipo_identificacion",
                        "label": "Tipo Identificacion",
                        "options": TIPOS_DE_IDENTIFICACION,
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                    {
                        "type": "text",
                        "name": "identificacion",
                        "label": "Numero Identificacion",
                        "sizing": {
                            "sm": 4
                        },
                        "validation": withIdentificationFormat(Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR)),
                        "suffixButton": true,
                        "suffix": <Button
                            className="rounded-0"
                            block
                            color="primary"
                            type="button"
                            onClick={() => OpenSearchClientByIdentificationPubSub.publish({ openOffCanvas: true })}>
                            Buscar
                        </Button>
                    }
                ],
                [
                    {
                        "type": "text",
                        "name": "direccion",
                        "label": "Dirección",
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 12
                            }
                        }
                    },
                ],
            ]
        }
    ],
};


export default InvoiceDataFormConfig;